import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronLeft, ChevronRight, Music, Share } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { apiRequest } from '@/lib/queryClient';
import type { TrackData } from './hooks/use-league-chat';

// Re-export TrackData type for convenience
export type { TrackData } from './hooks/use-league-chat';

interface TrackPickerProps {
  leagueId: number;
  onSelect: (track: TrackData) => void;
  disabled?: boolean;
}

interface Track {
  submissionId: number;
  roundId: number;
  roundName: string;
  title: string;
  artistName: string;
  albumName: string | null;
  thumbnailUrl: string | null;
  spotifyUrl: string | null;
  appleMusicUrl: string | null;
  youtubeMusicUrl: string | null;
  submittedBy: {
    userId: string;
    username: string;
    avatarUrl: string | null;
  };
}

interface Round {
  id: number;
  name: string;
  number: number;
  tracks: Track[];
}

interface TracksResponse {
  rounds: Round[];
}

export function TrackPicker({ leagueId, onSelect, disabled }: TrackPickerProps) {
  const [open, setOpen] = useState(false);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [selectedRound, setSelectedRound] = useState<Round | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch tracks when popover opens
  useEffect(() => {
    if (open && rounds.length === 0) {
      fetchTracks();
    }
  }, [open, leagueId]);

  const fetchTracks = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiRequest<TracksResponse>(`/api/leagues/${leagueId}/chat/tracks`);
      setRounds(response.rounds);
    } catch (err) {
      console.error('Failed to fetch tracks:', err);
      setError('Failed to load tracks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoundClick = (round: Round) => {
    setSelectedRound(round);
  };

  const handleBackClick = () => {
    setSelectedRound(null);
  };

  const handleTrackSelect = (track: Track) => {
    onSelect({
      submissionId: track.submissionId,
      roundId: track.roundId,
      roundName: track.roundName,
      title: track.title,
      artistName: track.artistName,
      albumName: track.albumName,
      thumbnailUrl: track.thumbnailUrl,
      spotifyUrl: track.spotifyUrl,
      appleMusicUrl: track.appleMusicUrl,
      youtubeMusicUrl: track.youtubeMusicUrl,
    });
    setOpen(false);
    setSelectedRound(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSelectedRound(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={disabled}
        >
          <Music className="h-4 w-4" />
          Track
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="top">
        <div className="flex flex-col max-h-[400px]">
          {/* Header */}
          <div className="p-3 border-b flex items-center gap-2">
            {selectedRound ? (
              <>
                <button
                  onClick={handleBackClick}
                  className="p-1 hover:bg-muted rounded"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="font-medium truncate">{selectedRound.name}</span>
              </>
            ) : (
              <>
                <Music className="h-4 w-4 text-primary" />
                <span className="font-medium">Share a Track</span>
              </>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: '320px' }}>
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                {error}
              </div>
            ) : rounds.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-sm text-muted-foreground p-4 text-center">
                <Music className="h-8 w-8 mb-2 opacity-50" />
                <p>No completed rounds yet</p>
                <p className="text-xs mt-1">Tracks will appear here after rounds are finished</p>
              </div>
            ) : selectedRound ? (
              // Track list for selected round
              <div className="divide-y">
                {selectedRound.tracks.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No tracks in this round
                  </div>
                ) : (
                  selectedRound.tracks.map((track) => (
                    <button
                      key={track.submissionId}
                      onClick={() => handleTrackSelect(track)}
                      className="w-full p-3 hover:bg-muted/50 transition-colors flex items-center gap-3 text-left"
                    >
                      {/* Album art */}
                      <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-muted">
                        {track.thumbnailUrl ? (
                          <img
                            src={track.thumbnailUrl}
                            alt={track.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Music className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Track info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm">{track.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{track.artistName}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Avatar className="h-4 w-4">
                            <AvatarImage src={track.submittedBy.avatarUrl || undefined} />
                            <AvatarFallback className="text-[8px]">
                              {track.submittedBy.username?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-muted-foreground truncate">
                            {track.submittedBy.username}
                          </span>
                        </div>
                      </div>

                      {/* Share button */}
                      <div className="flex-shrink-0">
                        <Share className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            ) : (
              // Round list
              <div className="divide-y">
                {rounds.map((round) => (
                  <button
                    key={round.id}
                    onClick={() => handleRoundClick(round)}
                    className="w-full p-3 hover:bg-muted/50 transition-colors flex items-center justify-between text-left"
                  >
                    <div>
                      <p className="font-medium text-sm">{round.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {round.tracks.length} track{round.tracks.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
